'use client';

import {
  useCallback,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from 'react';
import { ImagePlus, Loader2, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ipfsToHttp } from '@/lib/ipfs';
import { Input } from '@/components/ui/input';

interface IdentityCardProps {
  name: string;
  description: string;
  imageUrl: string;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
  onImageChange: (url: string) => void;
}

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const ACCEPT_HUMAN = 'PNG · JPG · WebP · GIF';
// Mirrors the `/api/ipfs/upload-image` server cap (`MAX_IMAGE_SIZE`).
// Bumping one without the other just trades a friendly client-side
// rejection for a confusing 400 from the route, so they stay locked
// until both are raised together.
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_BYTES_HUMAN = '5 MB';
const NAME_MAX = 42;
const DESC_MAX = 280;

/**
 * Combined Name + Description + Image upload card for the launch
 * wizard's Identity step.
 *
 * Replaces three sibling `<Field>`s with a single drag-drop surface so
 * the creator can drop a cover image straight onto the same card that
 * carries name + description — no field-shuffle, no separate "image"
 * step. The whole card is the drop target (not just the thumbnail) so
 * a hurried drop near the inputs still lands.
 *
 * Upload pipeline matches the rest of the app: file → base64 → POST
 * `/api/ipfs/upload-image` → Pinata gateway URL. The route already
 * enforces a 5 MB hard cap and runs NSFW detection (returning a
 * placeholder URL on flag); this component caps client-side at 8 MB so
 * the network round-trip is short-circuited for obviously oversized
 * files. GIFs are explicitly accepted.
 *
 * The component is presentation-only: it never touches the wizard
 * draft directly; the parent `IdentityStep` owns the state and threads
 * the three text/url values back in via the `on…Change` callbacks.
 */
export function IdentityCard({
  name,
  description,
  imageUrl,
  onNameChange,
  onDescriptionChange,
  onImageChange,
}: IdentityCardProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const previewUrl = imageUrl ? ipfsToHttp(imageUrl) ?? imageUrl : null;

  const uploadFile = useCallback(
    async (file: File) => {
      setError(null);
      if (!/^image\//.test(file.type)) {
        setError('File must be an image');
        return;
      }
      if (file.size > MAX_BYTES) {
        setError(`Image is over ${MAX_BYTES_HUMAN}`);
        return;
      }
      setBusy(true);
      try {
        const base64 = await fileToDataUrl(file);
        const res = await fetch('/api/ipfs/upload-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            base64Image: base64,
            contextLabel: 'board',
          }),
        });
        if (!res.ok) {
          const j = (await res.json().catch(() => ({}))) as {
            error?: string;
            message?: string;
          };
          throw new Error(j.error || j.message || `Upload failed (${res.status})`);
        }
        const json = (await res.json()) as { imageUrl?: string };
        if (!json.imageUrl) throw new Error('Upload returned no URL');
        onImageChange(json.imageUrl);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed');
      } finally {
        setBusy(false);
      }
    },
    [onImageChange],
  );

  const onFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset so picking the same file twice still fires `change`.
      event.target.value = '';
      if (file) void uploadFile(file);
    },
    [uploadFile],
  );

  // The drop handlers are attached to the *whole* card, not just the
  // thumbnail, so a creator can drag a file anywhere onto the
  // identity surface. We swallow the default to suppress the
  // browser's "open this file" navigation.
  const onCardDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      // Only flip the highlight when the drag actually carries files —
      // a stray text drag from a sibling field shouldn't pulse the
      // border.
      const hasFile = Array.from(event.dataTransfer?.types ?? []).includes('Files');
      if (hasFile) setDragOver(true);
    },
    [],
  );

  const onCardDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const onCardDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragOver(false);
      const file = event.dataTransfer?.files?.[0];
      if (file) void uploadFile(file);
    },
    [uploadFile],
  );

  const clearImage = useCallback(() => {
    onImageChange('');
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }, [onImageChange]);

  const openPicker = useCallback(() => {
    if (busy) return;
    inputRef.current?.click();
  }, [busy]);

  return (
    <div
      onDragOver={onCardDragOver}
      onDragLeave={onCardDragLeave}
      onDrop={onCardDrop}
      className={cn(
        'relative flex flex-col gap-3 rounded-md border bg-gradient-to-b from-zinc-950/70 to-black/60 p-4',
        dragOver
          ? 'border-amber-300/70 ring-1 ring-amber-300/30'
          : 'border-amber-400/15',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200/70">
          Identity
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-100/40">
          Drag to upload
        </span>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
        {/* Image drop zone — clickable for browse, also receives drops
            via the parent card handlers. The overlay state mirrors the
            board-cover style used in CreatorGridToolsPanel so the
            wizard preview reads as continuous. */}
        <button
          type="button"
          onClick={openPicker}
          aria-label={previewUrl ? 'Replace cover image' : 'Upload cover image'}
          className={cn(
            'group relative aspect-square w-full shrink-0 overflow-hidden rounded-[5px] border border-dashed transition-colors sm:w-[120px] md:w-[140px]',
            previewUrl
              ? 'border-solid border-amber-300/45'
              : 'border-amber-400/30 hover:border-amber-300/60 hover:bg-amber-400/5',
            dragOver && !previewUrl && 'border-amber-300/80 bg-amber-400/10',
            busy && 'cursor-progress',
          )}
        >
          {previewUrl ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={previewUrl}
                alt="Board cover preview"
                className="h-full w-full object-cover"
              />
              {busy && (
                <span className="absolute inset-0 grid place-items-center bg-black/60">
                  <Loader2 className="h-4 w-4 animate-spin text-amber-200" />
                </span>
              )}
              <span
                role="presentation"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  clearImage();
                }}
                aria-label="Remove image"
                className={cn(
                  'absolute right-1.5 top-1.5 grid h-5 w-5 place-items-center rounded-full',
                  'bg-black/80 text-amber-100/80 opacity-0 transition-opacity',
                  'group-hover:opacity-100 hover:text-amber-50',
                )}
              >
                <X className="h-3 w-3" />
              </span>
            </>
          ) : busy ? (
            <span className="grid h-full w-full place-items-center">
              <Loader2 className="h-5 w-5 animate-spin text-amber-200" />
            </span>
          ) : (
            <span className="grid h-full w-full place-items-center">
              <span className="flex flex-col items-center gap-1 text-amber-100/60 transition-colors group-hover:text-amber-50">
                <ImagePlus className="h-6 w-6" strokeWidth={1.4} />
                <span className="font-mono text-[8px] uppercase tracking-[0.18em]">
                  Drop or browse
                </span>
              </span>
            </span>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            onChange={onFileChange}
            disabled={busy}
            className="sr-only"
          />
        </button>

        {/* Name + description column. Stays a flex-col on mobile (image
            sits on top) and slots beside the thumbnail on ≥sm. */}
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="launch-name"
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200/70"
              >
                Name
              </label>
              <Counter current={name.length} max={NAME_MAX} />
            </div>
            <Input
              id="launch-name"
              value={name}
              maxLength={NAME_MAX}
              placeholder="The Boardroom"
              onChange={(e) => onNameChange(e.target.value)}
              className="border-amber-400/20 bg-black/50 text-amber-50"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <label
                htmlFor="launch-description"
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-200/70"
              >
                Description
              </label>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-amber-100/40">
                Optional
              </span>
            </div>
            <textarea
              id="launch-description"
              value={description}
              maxLength={DESC_MAX}
              placeholder="One line shown under the board name."
              rows={2}
              onChange={(e) => onDescriptionChange(e.target.value)}
              className="w-full resize-none rounded border border-amber-400/20 bg-black/50 px-3 py-2 text-[13px] text-amber-50 placeholder:text-amber-100/35 focus:border-amber-300/60 focus:outline-none"
            />
            <Counter current={description.length} max={DESC_MAX} />
          </div>
        </div>
      </div>

      {/* Footer: file-format hint + (when present) upload error.
          The hint sits below the columns so it doesn't compete with
          the field labels for the user's eye. */}
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-amber-100/40">
          {ACCEPT_HUMAN} · up to {MAX_BYTES_HUMAN}
        </p>
        {error ? (
          <p className="font-mono text-[10px] text-rose-300/90">{error}</p>
        ) : null}
      </div>
    </div>
  );
}

function Counter({ current, max }: { current: number; max: number }) {
  return (
    <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber-100/40">
      {current}/{max}
    </span>
  );
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('Failed to read file'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read error'));
    reader.readAsDataURL(file);
  });
}
